import React, {useCallback, useMemo, useState} from 'react';
import {Helmet} from 'react-helmet';
import HappyArrowCircleLeftIcon from '@stencil/icons/src/HappyArrowCircleLeftIcon/HappyArrowCircleLeftIcon';
import Carousel from '../../../common/Carousel/Carousel';
import {defaultTheme} from '@stencil/build-theme';
import {
  CenteredSpaceWrap,
  FlexCentered,
  FlexSpaceBetween,
  PrimaryLink,
} from '../../../../CommonStyleUtils/dwebCommon.styles';
import * as S from './CaptivateWideImageModal.style';
import CaptivateLandscapeImage from '../ImageOrientation/CaptivateLandscapeImg';

interface ImageData {
  url: string;
  mediaType?: string;
  userReview?: string;
  travelerName?: string;
  travelerRating?: number;
}

interface CarouselState {
  isOpen: boolean;
  currentTagId: string;
  imageNo: number;
}

interface CaptivateWideImageModalProps {
  data: ImageData[];
  updateCarouselState: (state: Partial<CarouselState>) => void;
  carouselState: CarouselState;
}

const GuestReview: React.FC<{item: ImageData; showAll: boolean; updateShowAll: () => void}> = ({
  item,
  showAll,
  updateShowAll,
}) => {
  return (
    <S.WideImageGuestReview>
      {item.userReview && item.travelerName && (
        <div>
          <CenteredSpaceWrap>
            <FlexCentered>
              <S.GuestAvatarName>{item.travelerName}</S.GuestAvatarName>
            </FlexCentered>
            {item.travelerRating > 0 && (
              <S.GuestRatingWrap totalRating={item.travelerRating}>
                {item.travelerRating}/5
              </S.GuestRatingWrap>
            )}
          </CenteredSpaceWrap>
          <S.GuestReviewDescription>
            {showAll ? item.userReview : item.userReview.substring(0, 250)}
            {!showAll && item.userReview.length > 250 && (
              <PrimaryLink onClick={updateShowAll}>
                <b>Read More</b>
              </PrimaryLink>
            )}
          </S.GuestReviewDescription>
        </div>
      )}
    </S.WideImageGuestReview>
  );
};

const CaptivateWideImageModal: React.FC<CaptivateWideImageModalProps> = ({
  data,
  updateCarouselState,
  carouselState,
}) => {
  const [showAll, setShowAll] = useState(false);

  const handleShowAll = useCallback(() => {
    setShowAll(true);
  }, []);

  const handleClose = useCallback(() => {
    updateCarouselState({isOpen: false, currentTagId: '', imageNo: 0});
  }, [updateCarouselState]);

  const renderItem = useCallback(
    (item: ImageData, index: number, isLastItem: boolean) => {
      const conditionalProps: {itemProp?: 'image'} = index === 0 ? {itemProp: 'image'} : {};

      return (
        <React.Fragment key={item.url || index}>
          <CaptivateLandscapeImage
            isFullView
            dataList={[item]}
            alt="Main gallery"
            listItemWidth="100%"
            {...conditionalProps}
          />
          <GuestReview item={item} showAll={showAll} updateShowAll={handleShowAll} />
        </React.Fragment>
      );
    },
    [showAll, handleShowAll],
  );

  const firstImage = data?.[0]?.url;

  return (
    <S.WideImageModalWrap>
      {firstImage && (
        <Helmet>
          <link rel="preload" as="image" href={firstImage} />
        </Helmet>
      )}
      <FlexSpaceBetween>
        <HappyArrowCircleLeftIcon
          width="3rem"
          height="3rem"
          fill={defaultTheme.colors.downriver.base}
          margin="0 0 4.5rem 0"
          onClick={handleClose}
        />
      </FlexSpaceBetween>
      <Carousel
        options={{emblaOptions: {startIndex: carouselState.imageNo - 1 || 0}, topPosition: '70%'}}
        preloadImagesOnInteraction
        data={data}
        renderItem={renderItem}
      />
    </S.WideImageModalWrap>
  );
};

export default CaptivateWideImageModal;
